/**
 * homepageConfigCache — two-layer cache for `/homepage-config`.
 *
 * Layer 1 — module-level (`cache`): survives across in-page navigations.
 * Layer 2 — `localStorage` with TTL: survives across hard-reloads (F5) and
 * new tabs. Reduces perceived "site is slow" on refresh from ~300ms + JSON
 * parse to <5ms.
 *
 * Usage:
 *   const cfg = await fetchHomepageConfig();
 * Invalidate on admin PUT/import:
 *   invalidateHomepageConfig();
 */
import { api } from "@/lib/api";

const LS_KEY = "supertmatik.homepageConfig.v1";
const LS_TS_KEY = "supertmatik.homepageConfig.v1.ts";
const TTL_MS = 5 * 60 * 1000; // 5 minutes — long enough for a refresh burst,
                              // short enough that admin edits show up quickly.

let cache = null;      // In-memory copy for the current tab session
let inflight = null;   // Dedupe concurrent callers
let preloadInjected = false; // Guard so we only inject <link rel="preload"> once per page

/**
 * Inject <link rel="preload" as="image"> tags for the LCP hero images
 * (AI title, mascot, side image, background). Called as early as possible
 * from the sync cache reader so the browser starts fetching them BEFORE
 * React mounts. Idempotent — safe to call multiple times.
 *
 * SCOPING: only fires when the current pathname is the Homepage ("/"). The
 * config cache is also consumed by /treinar, /vs-robot, /jogar, etc.
 * (they read `logo_url` + brand text for the game chrome), but the HERO
 * images are only rendered on Home — preloading them elsewhere just
 * produces "…preloaded but not used" console warnings.
 */
function injectHeroPreloads(cfg) {
  if (preloadInjected || typeof document === "undefined" || !cfg) return;
  try {
    const path = (typeof window !== "undefined" && window.location && window.location.pathname) || "";
    if (path !== "/" && path !== "") return; // only preload heroes on the homepage
  } catch { /* proceed */ }
  const backend = (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) || "";
  const resolve = (u) => (u && u.startsWith("/") ? `${backend}${u}` : u);
  const urls = [
    resolve(cfg?.hero?.title_image_url),
    resolve(cfg?.hero?.center_image_url),  // mascot
    resolve(cfg?.hero?.side_image_url),
    resolve(cfg?.site_theme?.bg_image_url),
  ].filter(Boolean);
  if (urls.length === 0) return;
  try {
    const head = document.head;
    urls.forEach((href) => {
      // Skip if already present
      if (head.querySelector(`link[rel="preload"][href="${href}"]`)) return;
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = href;
      // High fetch priority hint (Chrome / Edge). Ignored gracefully by others.
      link.setAttribute("fetchpriority", "high");
      head.appendChild(link);
    });
    preloadInjected = true;
  } catch {
    // Non-fatal — page will still work without preload hints.
  }
}

function readLocal() {
  try {
    const ts = Number(localStorage.getItem(LS_TS_KEY) || 0);
    if (!ts || Date.now() - ts > TTL_MS) return null;
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Synchronous accessor used by React's `useState` initializer so components
 * can paint the homepage in the SAME frame as the intro splash on F5.
 * Returns the cached config or `null` (never throws, never fetches).
 */
export function readHomepageConfigCacheSync() {
  const local = readLocal();
  if (local) cache = local;    // prime module-level cache too
  // Kick off <link rel="preload"> injection for hero images ASAP so the
  // browser starts fetching them in parallel with React bootstrap.
  if (cache) injectHeroPreloads(cache);
  return cache;
}

function writeLocal(data) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(data));
    localStorage.setItem(LS_TS_KEY, String(Date.now()));
  } catch {
    // Storage may be full or unavailable (private mode) — ignore, module cache still works.
  }
}

export async function fetchHomepageConfig() {
  if (cache) {
    injectHeroPreloads(cache);
    return cache;
  }
  // Try localStorage synchronously — instant paint on F5.
  const local = readLocal();
  if (local) {
    cache = local;
    injectHeroPreloads(cache);
    // Background revalidation: refresh in the background so next navigation is fresh.
    api.get("/homepage-config").then((r) => {
      if (r?.data) {
        cache = r.data;
        writeLocal(r.data);
      }
    }).catch(() => {});
    return cache;
  }
  if (inflight) return inflight;
  inflight = api
    .get("/homepage-config")
    .then((r) => {
      cache = r.data || {};
      writeLocal(cache);
      injectHeroPreloads(cache);
      inflight = null;
      return cache;
    })
    .catch((err) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

export function invalidateHomepageConfig() {
  cache = null;
  inflight = null;
  preloadInjected = false;
  try {
    localStorage.removeItem(LS_KEY);
    localStorage.removeItem(LS_TS_KEY);
  } catch {
    // ignore
  }
}
